import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Save, ShieldAlert, Loader2, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin-only page for rotating Resend credentials without a re-publish.
 * DB-first: notifications.py reads `app_settings.resend_config` (this record)
 * before falling back to env vars.
 */
export default function ResendConfigAdmin() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ api_key: "", from_email: "" });
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/resend-config");
      setInfo(data);
      setForm({ api_key: "", from_email: data.from_email || "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    // Allow FROM-only updates when a key is already on file — makes rotating the address 1-click
    const isKeyOnFile = !!(info?.configured);
    if (!isKeyOnFile && (!form.api_key.trim() || form.api_key.trim().length < 8)) {
      return toast.error("First save requires an API key (min 8 chars).");
    }
    if (form.api_key.trim() && form.api_key.trim().length < 8) {
      return toast.error("API key looks too short.");
    }
    if (!form.from_email.trim()) return toast.error("From email is required.");
    setSaving(true);
    try {
      const body = { from_email: form.from_email.trim() };
      if (form.api_key.trim()) body.api_key = form.api_key.trim();
      const { data } = await api.put("/admin/resend-config", body);
      toast.success(`Saved — FROM is now ${data.active.from_email}`);
      setForm({ ...form, api_key: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    const to = (testTo || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) return toast.error("Enter a valid test recipient email.");
    setTesting(true);
    try {
      const { data } = await api.post("/admin/resend-config/test", { to });
      toast.success(`Sent ✓ (delivery id ${data.delivery_id?.slice(0, 8)}…)`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-8" data-testid="resend-config-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-neutral-900">Resend credentials</h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-xl leading-relaxed">
          Rotate the Resend API key + FROM address here. Changes are saved to the database and take effect
          immediately — no re-publish needed. DB values override the deployed environment integration.
        </p>
      </div>

      {/* Active status card */}
      <div className="bg-white border border-neutral-200 p-6 mb-6" data-testid="resend-active-card">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Currently active</div>
          <button onClick={load} disabled={loading} className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1" data-testid="resend-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : info ? (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <StatLine label="Source"     value={info.source === "db" ? "🟢 DB override" : "🟡 Env fallback"} />
            <StatLine label="Configured" value={info.configured ? "Yes" : "No"} />
            <StatLine label="From"       value={info.from_email || "—"} />
            <StatLine label="Key suffix" value={info.key_last4 ? `…${info.key_last4}` : "—"} mono />
            <StatLine label="Env key present" value={info.env_key_present ? `…${info.env_key_last4}` : "no"} />
          </div>
        ) : null}
      </div>

      {/* Update form */}
      <form onSubmit={save} className="bg-white border border-neutral-200 p-6 mb-6" data-testid="resend-form">
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">Update credentials</div>
        <label className="block mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">
            New API key {info?.configured && <span className="normal-case tracking-normal text-neutral-400 lowercase">— leave blank to keep current key</span>}
          </div>
          <input
            type="password"
            value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            placeholder={info?.configured ? "(leave blank to update only the FROM address)" : "re_XXXXXXXXXX…"}
            className="lf-input font-mono w-full"
            data-testid="resend-input-key"
            autoComplete="off"
          />
        </label>
        <label className="block mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">From address (verified domain)</div>
          <input
            type="text"
            value={form.from_email}
            onChange={(e) => setForm({ ...form, from_email: e.target.value })}
            placeholder="Clients OnRamp <aiaudits@clientsonramp.com>"
            className="lf-input w-full"
            data-testid="resend-input-from"
          />
        </label>
        <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 p-3 mb-4">
          <ShieldAlert className="w-4 h-4 mt-0.5 text-amber-700 flex-shrink-0" />
          <div>The domain in your FROM address must be verified in your Resend account (Domains section) — otherwise every send fails with "You can only send testing emails to your own email address".</div>
        </div>
        <button type="submit" disabled={saving} className="lf-btn-primary inline-flex items-center gap-2" data-testid="resend-save-btn">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save credentials"}
        </button>
      </form>

      {/* Test send */}
      <div className="bg-white border border-neutral-200 p-6" data-testid="resend-test-card">
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">Send a test email</div>
        <div className="flex gap-3">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@yourdomain.com"
            className="lf-input flex-1"
            data-testid="resend-test-to"
          />
          <button onClick={test} disabled={testing || !info?.configured} className="lf-btn-outline inline-flex items-center gap-2 whitespace-nowrap" data-testid="resend-test-btn">
            <Send className="w-4 h-4" /> {testing ? "Sending…" : "Send test"}
          </button>
        </div>
        <p className="text-xs text-neutral-500 mt-3">Uses the currently-active configuration. A successful send confirms the key + domain are working end-to-end.</p>
      </div>
    </div>
  );
}

function StatLine({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`text-sm text-neutral-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
